import React, { createContext, useCallback, useContext, useRef } from 'react';
import { EMERGENCY_NUMBER, VIOLETA_LINES, VIOLETA_LINES_HOURS } from '../config/site';
import Icon from './Icon';
import { QuickExitLink } from './QuickExit';

type HelpApi = { open: () => void };
const HelpContext = createContext<HelpApi>({ open: () => undefined });

export const useHelp = (): HelpApi => useContext(HelpContext);

/**
 * "Ayuda ahora": 911 first, then Violeta's 4 lines. Native <dialog>: focus trap, Esc and backdrop for free.
 * Without JavaScript the same buttons link to /ayuda.
 */
export const HelpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dialog = useRef<HTMLDialogElement>(null);

  const open = useCallback(() => {
    const el = dialog.current;
    if (el && typeof el.showModal === 'function' && !el.open) el.showModal();
  }, []);
  const close = useCallback(() => dialog.current?.close(), []);

  return (
    <HelpContext.Provider value={{ open }}>
      {children}
      <dialog
        ref={dialog}
        className="dialog"
        aria-labelledby="dlg-title"
        onClick={(event) => {
          if (event.target === dialog.current) close();
        }}
      >
        <div className="dialog__head">
          <div>
            <h2 id="dlg-title">Ayuda ahora</h2>
            <p>Elige lo que te sirva. No estás sola.</p>
          </div>
          <button className="dialog__close" type="button" onClick={close} aria-label="Cerrar">
            <Icon name="x" />
          </button>
        </div>
        <div className="dialog__body">
          <a className="dial dial--urgent" href={`tel:${EMERGENCY_NUMBER}`}>
            <span className="dial__num">{EMERGENCY_NUMBER}</span>
            <span>
              <b>Emergencias</b>
              <span>Si estás en peligro ahora.</span>
            </span>
            <span className="dial__go"><Icon name="phone" />Llamar</span>
          </a>
          <p className="dialog__hours">
            Las líneas de Violeta atienden {VIOLETA_LINES_HOURS} Fuera de ese horario, llama al {EMERGENCY_NUMBER}.
          </p>
          {VIOLETA_LINES.map((line) => (
            <a className="dial dial--compact" key={line.tel} href={`tel:${line.tel}`}>
              <b className="dial__line">{line.label}</b>
              <span className="dial__label">Línea de Violeta</span>
              <span className="dial__go"><Icon name="phone" />Llamar</span>
            </a>
          ))}
        </div>
        <div className="dialog__foot">
          <span>Violeta no es un servicio de emergencias.</span>
          <QuickExitLink className="quick-exit quick-exit--dark" />
        </div>
      </dialog>
    </HelpContext.Provider>
  );
};
